import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';

/* ─── Constants ──────────────────────────────────────────────────── */
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAYS_SHORT  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const HEADER_H = 52;

const EVENT_CFG = {
  'Bullish':      { bg: '#16a34a', glow: 'rgba(34,197,94,0.35)',   pill: 'rgba(22,163,74,0.18)'  },
  'Bearish':      { bg: '#dc2626', glow: 'rgba(239,68,68,0.35)',   pill: 'rgba(220,38,38,0.18)'  },
  'As per Trend': { bg: '#2563eb', glow: 'rgba(59,130,246,0.35)',  pill: 'rgba(37,99,235,0.18)'  },
  'Sell On Rise': { bg: '#991b1b', glow: 'rgba(153,27,27,0.4)',    pill: 'rgba(153,27,27,0.2)'   },
  'Buy On Dip':   { bg: '#15803d', glow: 'rgba(21,128,61,0.35)',   pill: 'rgba(21,128,61,0.18)'  },
  'Reversal Day': { bg: '#ea580c', glow: 'rgba(234,88,12,0.35)',   pill: 'rgba(234,88,12,0.18)'  },
};

const MOON_ICON = {
  'Amavasya':      '🌑',
  'Poonam':        '🌕',
  'Shukla Paksha': '🌒',
  'Krishna Paksha':'🌘',
};

const STARS = Array.from({ length: 60 }, (_, i) => ({
  id: i,
  left:  `${Math.random() * 100}%`,
  top:   `${Math.random() * 100}%`,
  delay: `${(Math.random() * 4).toFixed(2)}s`,
  size:  Math.random() > 0.8 ? 3 : 2,
}));

/* ─── Helpers ────────────────────────────────────────────────────── */
function toKey(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

/* ─── EventPill ──────────────────────────────────────────────────── */
function EventPill({ event }) {
  const cfg = EVENT_CFG[event.eventType] || { bg: '#6366f1', pill: 'rgba(99,102,241,0.18)' };
  return (
    <div style={{
      background: cfg.pill,
      borderLeft: `3px solid ${cfg.bg}`,
      padding: '2px 5px',
      borderRadius: 3,
      fontSize: '0.6rem',
      lineHeight: 1.3,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#fff',
      maxWidth: '100%',
    }}>
      {event.stockName || event.indexName || event.eventType}
    </div>
  );
}

/* ─── FestPill ───────────────────────────────────────────────────── */
function FestPill({ fest }) {
  return (
    <div style={{
      background: fest.isMarketHoliday ? 'rgba(239,68,68,0.15)' : 'rgba(251,191,36,0.15)',
      borderLeft: `3px solid ${fest.isMarketHoliday ? '#ef4444' : '#fbbf24'}`,
      padding: '2px 5px',
      borderRadius: 3,
      fontSize: '0.6rem',
      lineHeight: 1.3,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#fff',
      maxWidth: '100%',
    }}>
      {fest.name}
    </div>
  );
}

/* ─── DayCell ────────────────────────────────────────────────────── */
function DayCell({ date, isOtherMonth, isToday, isSunday, dayEvents, dayFests, onClick }) {
  const hasItems = dayEvents.length > 0 || dayFests.length > 0;
  const moonEv = dayEvents.find(e => e.moonPhase && MOON_ICON[e.moonPhase]);
  const isHoliday = dayFests.some(f => f.isMarketHoliday);
  const maxPills = 3;
  const allPills = [
    ...dayFests.map(f => ({ type: 'fest', data: f })),
    ...dayEvents.map(e => ({ type: 'event', data: e })),
  ];
  const overflow = allPills.length - maxPills;

  return (
    <div
      onClick={() => hasItems && onClick(toKey(date))}
      style={{
        ...S.dayCell,
        ...(isOtherMonth ? { opacity: 0.3 } : {}),
        ...(isToday ? { border: '1px solid rgba(99,102,241,0.5)', background: 'rgba(99,102,241,0.08)' } : {}),
        ...(isHoliday ? { background: 'rgba(239,68,68,0.06)' } : {}),
        cursor: hasItems ? 'pointer' : 'default',
      }}
    >
      <div style={S.dayCellTop}>
        <span style={{
          ...S.dayNum,
          ...(isSunday ? { color: '#f87171' } : {}),
          ...(isToday ? { color: '#a78bfa', fontWeight: 700 } : {}),
        }}>
          {date.getDate()}
        </span>
        {moonEv && <span style={{ fontSize: '0.7rem' }}>{MOON_ICON[moonEv.moonPhase]}</span>}
      </div>
      <div style={S.pillsArea}>
        {allPills.slice(0, maxPills).map((p, i) =>
          p.type === 'fest'
            ? <FestPill key={i} fest={p.data} />
            : <EventPill key={i} event={p.data} />
        )}
        {overflow > 0 && (
          <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', paddingLeft: 4 }}>
            +{overflow} more
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── MonthGrid ──────────────────────────────────────────────────── */
function MonthGrid({ viewDate, events, festivals, today, onDayClick }) {
  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay  = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  // pad with previous month days
  for (let i = 0; i < firstDay; i++) {
    const d = new Date(year, month, 0 - (firstDay - 1 - i));
    cells.push({ date: d, isOtherMonth: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), isOtherMonth: false });
  }
  // pad to complete rows
  const remaining = (7 - (cells.length % 7)) % 7;
  for (let i = 1; i <= remaining; i++) {
    cells.push({ date: new Date(year, month + 1, i), isOtherMonth: true });
  }

  return (
    <div style={S.monthGrid}>
      {cells.map((c, i) => {
        const k = toKey(c.date);
        return (
          <DayCell
            key={i}
            date={c.date}
            isOtherMonth={c.isOtherMonth}
            isToday={sameDay(c.date, today)}
            isSunday={c.date.getDay() === 0}
            dayEvents={events[k] || []}
            dayFests={festivals[k] || []}
            onClick={onDayClick}
          />
        );
      })}
    </div>
  );
}

/* ─── WeekGrid ───────────────────────────────────────────────────── */
function WeekGrid({ focusDay, events, festivals, today, onDayClick }) {
  const startOfWeek = new Date(focusDay);
  startOfWeek.setDate(focusDay.getDate() - focusDay.getDay());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  return (
    <div style={S.weekGrid}>
      {days.map((d, i) => {
        const k = toKey(d);
        return (
          <DayCell
            key={i}
            date={d}
            isOtherMonth={false}
            isToday={sameDay(d, today)}
            isSunday={d.getDay() === 0}
            dayEvents={events[k] || []}
            dayFests={festivals[k] || []}
            onClick={onDayClick}
          />
        );
      })}
    </div>
  );
}

/* ─── DayView ────────────────────────────────────────────────────── */
function DayView({ focusDay, events, festivals }) {
  const k = toKey(focusDay);
  const dayEvents  = events[k] || [];
  const dayFests   = festivals[k] || [];
  const isEmpty    = !dayEvents.length && !dayFests.length;

  return (
    <div style={S.dayViewWrap}>
      {isEmpty ? (
        <div style={S.centeredMsg}>
          <span style={{ fontSize: '2.5rem', opacity: 0.25 }}>📭</span>
          <p style={{ marginTop: 12, color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
            No events for this day.
          </p>
        </div>
      ) : (
        <>
          {dayFests.map((f, i) => (
            <div key={i} style={{
              ...S.detailCard,
              borderLeft: `4px solid ${f.isMarketHoliday ? '#ef4444' : '#fbbf24'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}>{f.name}</span>
                {f.isMarketHoliday && <span style={S.holidayBadge}>Market Holiday</span>}
              </div>
              <div style={S.detailMeta}>{f.type} · {f.description || ''}</div>
            </div>
          ))}
          {dayEvents.map((e, i) => {
            const cfg = EVENT_CFG[e.eventType] || { bg: '#6366f1', pill: 'rgba(99,102,241,0.15)' };
            return (
              <div key={i} style={{ ...S.detailCard, borderLeft: `4px solid ${cfg.bg}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}>
                    {e.stockName || e.indexName || '—'}
                  </span>
                  <span style={{
                    ...S.typeBadge,
                    background: cfg.bg,
                    boxShadow: `0 0 8px ${cfg.glow}`,
                  }}>{e.eventType}</span>
                </div>
                {e.description && <div style={S.detailMeta}>{e.description}</div>}
                {e.moonPhase && <div style={S.moonLine}>{MOON_ICON[e.moonPhase] || ''} {e.moonPhase}</div>}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ─── DayModal ───────────────────────────────────────────────────── */
function DayModal({ modal, onClose }) {
  const dateObj = new Date(modal.dateStr + 'T00:00:00');
  const label   = dateObj.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <span style={S.modalTitle}>📅 {label}</span>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={S.modalBody}>
          {modal.festivals.map((f, i) => (
            <div key={i} style={{
              ...S.modalItem,
              borderLeft: `4px solid ${f.isMarketHoliday ? '#ef4444' : '#fbbf24'}`,
            }}>
              <div style={S.modalItemTitle}>{f.name}</div>
              {f.isMarketHoliday && <span style={S.holidayBadge}>Market Holiday</span>}
              {f.description && <div style={S.modalItemDesc}>{f.description}</div>}
            </div>
          ))}
          {modal.events.map((e, i) => {
            const cfg = EVENT_CFG[e.eventType] || { bg: '#6366f1', glow: 'rgba(99,102,241,0.3)' };
            return (
              <div key={i} style={{ ...S.modalItem, borderLeft: `4px solid ${cfg.bg}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={S.modalItemTitle}>{e.stockName || e.indexName || 'Market Event'}</span>
                  <span style={{ ...S.typeBadge, background: cfg.bg, boxShadow: `0 0 6px ${cfg.glow}` }}>
                    {e.eventType}
                  </span>
                </div>
                {e.description && <div style={S.modalItemDesc}>{e.description}</div>}
                {e.moonPhase && <div style={S.moonLine}>{MOON_ICON[e.moonPhase] || ''} {e.moonPhase}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function AstroCalendar() {
  const today = new Date();
  const [viewDate, setViewDate]   = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [viewMode, setViewMode]   = useState('month');
  const [focusDay, setFocusDay]   = useState(new Date(today));
  const [evData, setEvData]       = useState({});
  const [festData, setFestData]   = useState({});
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [modal, setModal]         = useState(null);

  const fetchData = useCallback(async (month, year) => {
    setLoading(true);
    setError('');
    try {
      const [evRes, festRes] = await Promise.all([
        api.get(`/astro-calendar/events?month=${month}&year=${year}`),
        api.get(`/astro-calendar/festivals?month=${month}&year=${year}`),
      ]);
      const evMap = {};
      (evRes.data.data || []).forEach(ev => {
        const k = toKey(ev.date);
        if (!evMap[k]) evMap[k] = [];
        evMap[k].push(ev);
      });
      const festMap = {};
      (festRes.data.data || []).forEach(f => {
        const k = toKey(f.date);
        if (!festMap[k]) festMap[k] = [];
        festMap[k].push(f);
      });
      setEvData(evMap);
      setFestData(festMap);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(viewDate.getMonth() + 1, viewDate.getFullYear());
  }, [viewDate, fetchData]);

  const navigate = (delta) => {
    if (viewMode === 'month') {
      setViewDate(d => new Date(d.getFullYear(), d.getMonth() + delta, 1));
    } else if (viewMode === 'week') {
      setFocusDay(d => {
        const nd = new Date(d);
        nd.setDate(nd.getDate() + delta * 7);
        setViewDate(new Date(nd.getFullYear(), nd.getMonth(), 1));
        return nd;
      });
    } else {
      setFocusDay(d => {
        const nd = new Date(d);
        nd.setDate(nd.getDate() + delta);
        setViewDate(new Date(nd.getFullYear(), nd.getMonth(), 1));
        return nd;
      });
    }
  };

  const goToday = () => {
    setFocusDay(new Date(today));
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const handleDayClick = (dateStr) => {
    const de = evData[dateStr] || [];
    const df = festData[dateStr] || [];
    if (de.length || df.length) setModal({ dateStr, events: de, festivals: df });
  };

  const periodLabel =
    viewMode === 'month'
      ? `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`
      : viewMode === 'week'
      ? `Week of ${focusDay.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : focusDay.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  /* total stats for the month strip */
  const totalEvents   = Object.values(evData).flat().length;
  const totalFests    = Object.values(festData).flat().length;
  const totalHolidays = Object.values(festData).flat().filter(f => f.isMarketHoliday).length;

  return (
    <AppLayout pageBadge="Astro Calendar">

      {/* ── Starfield ── */}
      <div style={S.starfield}>
        {STARS.map(s => (
          <div key={s.id} style={{
            ...S.star, left: s.left, top: s.top, animationDelay: s.delay, width: s.size, height: s.size,
          }} />
        ))}
      </div>

      {/* ── No-scroll shell ── */}
      <div style={{ ...S.shell, height: `calc(100vh - ${HEADER_H}px)` }}>

        {/* ══ Row 1 — Controls ════════════════════════════════════ */}
        <div style={S.controlsBar}>
          <div style={S.barLeft}>
            <span style={{ fontSize: '1.2rem' }}>🌙</span>
            <span style={S.pageTitle}>Astro Trading Calendar</span>
            <span style={S.pageSub}>Astrological insights for trading</span>
          </div>

          {/* month nav */}
          <div style={S.navGroup}>
            <button style={S.navBtn} onClick={() => navigate(-1)}>‹</button>
            <span style={S.periodLabel}>{periodLabel}</span>
            <button style={S.navBtn} onClick={() => navigate(1)}>›</button>
            <button style={S.todayBtn} onClick={goToday}>📅 Today</button>
          </div>

          {/* view mode */}
          <div style={S.viewBtns}>
            {['month','week','day'].map(m => (
              <button
                key={m}
                style={{ ...S.viewBtn, ...(viewMode === m ? S.viewBtnActive : {}) }}
                onClick={() => setViewMode(m)}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* ══ Row 2 — Stats + Legend ══════════════════════════════ */}
        <div style={S.legendBar}>
          {/* mini stats */}
          <div style={S.statChip}><span style={{ color: '#a78bfa' }}>📌</span> {totalEvents} Events</div>
          <div style={S.statChip}><span style={{ color: '#fbbf24' }}>🎉</span> {totalFests} Festivals</div>
          <div style={S.statChip}><span style={{ color: '#ef4444' }}>🔴</span> {totalHolidays} Holidays</div>
          <div style={S.legendDivider} />
          {/* legend dots */}
          {Object.entries(EVENT_CFG).map(([type, c]) => (
            <div key={type} style={S.legendItem}>
              <span style={{ ...S.legendDot, background: c.bg }} />
              <span style={S.legendText}>{type}</span>
            </div>
          ))}
          <div style={S.legendItem}>
            <span style={{ ...S.legendDot, background: '#fbbf24' }} />
            <span style={S.legendText}>Festival</span>
          </div>
        </div>

        {/* ══ Row 3 — Day name headers (month + week) ═════════════ */}
        {viewMode !== 'day' && (
          <div style={S.dayHeaders}>
            {DAYS_SHORT.map(d => (
              <div key={d} style={{
                ...S.dayHeaderCell,
                ...(d === 'Sun' ? { color: '#f87171' } : {}),
              }}>{d}</div>
            ))}
          </div>
        )}

        {/* ══ Row 4 — Calendar area (flex:1) ══════════════════════ */}
        <div style={S.calArea}>
          {loading ? (
            <div style={S.centeredMsg}>
              <div style={S.loaderRing} />
              <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                Loading calendar data…
              </p>
            </div>
          ) : error ? (
            <div style={S.centeredMsg}>
              <span style={{ fontSize: '2rem', opacity: 0.35 }}>⚠️</span>
              <p style={{ marginTop: 12, color: '#fca5a5', fontSize: '0.82rem' }}>{error}</p>
            </div>
          ) : viewMode === 'month' ? (
            <MonthGrid viewDate={viewDate} events={evData} festivals={festData} today={today} onDayClick={handleDayClick} />
          ) : viewMode === 'week' ? (
            <WeekGrid focusDay={focusDay} events={evData} festivals={festData} today={today} onDayClick={handleDayClick} />
          ) : (
            <DayView focusDay={focusDay} events={evData} festivals={festData} today={today} />
          )}
        </div>

      </div>{/* /shell */}

      {/* ── Day Modal ── */}
      {modal && <DayModal modal={modal} onClose={() => setModal(null)} />}

    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  starfield: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
  },
  star: {
    position: 'absolute', background: '#fff', borderRadius: '50%',
    animation: 'twinkle 3s infinite',
  },

  /* shell */
  shell: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    padding: '10px 14px 10px',
    marginTop: HEADER_H,
    gap: 7,
    color: '#fff',
    fontFamily: "'Poppins','Segoe UI',sans-serif",
  },

  /* ── Row 1: Controls ── */
  controlsBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12,
    padding: '9px 16px',
  },
  barLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  pageTitle: { fontSize: '0.95rem', fontWeight: 700 },
  pageSub: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' },

  navGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  navBtn: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', width: 32, height: 32,
    borderRadius: 8, fontSize: '1.1rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s',
  },
  periodLabel: {
    fontSize: '0.88rem', fontWeight: 600, minWidth: 160, textAlign: 'center',
    background: 'linear-gradient(135deg,#a78bfa,#60a5fa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  todayBtn: {
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.3)',
    color: '#a78bfa', padding: '5px 14px',
    borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer',
  },
  viewBtns: { display: 'flex', gap: 4 },
  viewBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.6)', padding: '5px 14px',
    borderRadius: 8, fontSize: '0.78rem', cursor: 'pointer',
    transition: 'all 0.2s',
  },
  viewBtnActive: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    borderColor: 'transparent', color: '#fff',
    boxShadow: '0 0 12px rgba(102,126,234,0.4)',
  },

  /* ── Row 2: Legend ── */
  legendBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10, padding: '7px 14px',
  },
  statChip: {
    fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)',
    display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
  },
  legendDivider: {
    width: 1, height: 16, background: 'rgba(255,255,255,0.1)', flexShrink: 0,
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  legendDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  legendText: { fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' },

  /* ── Row 3: Day headers ── */
  dayHeaders: {
    flexShrink: 0,
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 3,
  },
  dayHeaderCell: {
    textAlign: 'center', fontSize: '0.72rem', fontWeight: 600,
    color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
    letterSpacing: 0.8, padding: '4px 0',
  },

  /* ── Row 4: Calendar area ── */
  calArea: {
    flex: 1, minHeight: 0,
    overflowY: 'auto', overflowX: 'hidden',
  },

  /* month grid */
  monthGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 3,
    height: '100%',
  },

  /* week grid */
  weekGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 3, height: '100%',
  },

  /* day cell */
  dayCell: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8,
    padding: '5px 6px',
    minHeight: 80,
    display: 'flex', flexDirection: 'column', gap: 3,
    transition: 'border-color 0.2s, background 0.2s',
    overflow: 'hidden',
  },
  dayCellTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 2,
  },
  dayNum: {
    fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)',
    lineHeight: 1,
  },
  pillsArea: {
    display: 'flex', flexDirection: 'column', gap: 2,
    overflow: 'hidden',
  },

  /* day view */
  dayViewWrap: {
    display: 'flex', flexDirection: 'column', gap: 10,
    padding: '4px 2px',
    maxWidth: 680, margin: '0 auto', width: '100%',
  },
  detailCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12, padding: '14px 16px',
  },
  detailMeta: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: 5 },
  holidayBadge: {
    background: 'rgba(239,68,68,0.2)', color: '#f87171',
    padding: '2px 8px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600,
  },
  typeBadge: {
    color: '#fff', padding: '2px 10px', borderRadius: 20,
    fontSize: '0.7rem', fontWeight: 600, flexShrink: 0,
  },
  moonLine: {
    fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: 5,
  },

  /* loader / empty */
  centeredMsg: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', minHeight: 200,
  },
  loaderRing: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid rgba(99,102,241,0.2)',
    borderTopColor: '#667eea',
    animation: 'spin 0.9s linear infinite',
  },

  /* modal */
  modalOverlay: {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 16,
    width: '100%', maxWidth: 520,
    maxHeight: '80vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    flexShrink: 0,
  },
  modalTitle: { fontWeight: 600, fontSize: '0.92rem' },
  modalClose: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', width: 28, height: 28,
    borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem',
  },
  modalBody: {
    padding: '14px 20px',
    overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  modalItem: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10, padding: '12px 14px',
  },
  modalItemTitle: { fontWeight: 600, fontSize: '0.88rem', color: '#fff', marginBottom: 4 },
  modalItemDesc: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginTop: 4 },
};
